import React, { useCallback, useEffect, useState } from 'react';
import PageHeaderText from 'components/base/page-header-text';
import { Button, ToastMessage } from '@cokitchen/cokitchen-components';
import { Link } from 'react-router-dom';
import RequestListFilters from 'components/base/request-list-filters';
import { ReactComponent as NoProcurement } from 'assets/icons/no-procurement.svg';
import { MAIN_STORE_ROUTE_KEYS } from 'utils/constants';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import ActivityIndicator from 'components/base/loader/activityIndicator';
import { ENUM_REQUEST_STATUS } from 'models/common.model';
import { debounce } from 'utils/functions';
import KitchenProcurementCard from 'components/base/procurement-card/kitchenProcurementCard';
import useDashboardContext from 'contexts/dashboard.context';
import { SocketIOConnection } from 'utils/socket-config';
import { RequestType } from 'models/item-request.model';
import useRoleContext from 'contexts/role.context';
import { InventoryModule, RolePermission } from 'models/permission.model';

export type DateRangeType = {
  startDate: Date;
  endDate: Date;
};

type RequestStatusFilter = ENUM_REQUEST_STATUS;

type KitchenProcurementFilters = {
  search: string;
  status: RequestStatusFilter[];
  dateRange: DateRangeType | null;
  current_page: number;
};

const KitchenProcurementList = () => {
  const [isFetchingProcurements, setIsFetchingProcurements] = useState(false);
  const [procurements, setProcurements] = useState<RequestType[]>([]);
  const [lastPage, setLastPage] = useState(1);
  const [filters, setFilters] = useState<KitchenProcurementFilters>({
    search: '',
    status: [],
    dateRange: null,
    current_page: 1
  });
  const { activeStore } = useDashboardContext();
  const { can } = useRoleContext();
  const canWriteProcurement = can(InventoryModule.KitchenRequisition, RolePermission.Write);
  const fetchProcurements = async (
    searchString: string,
    status: RequestStatusFilter[],
    current_page?: number,
    startDate?: Date,
    endDate?: Date
  ) => {
    if (!activeStore?.id) return;
    try {
      setIsFetchingProcurements(true);
      const statusParam = status.length === 1 ? status[0] : status;
      const response = await apiService.get(`kitchen-store/${activeStore.id}/requests`, {
        version: 'v2',
        params: {
          code: searchString,
          status: statusParam,
          page: current_page || '1',
          created_at_after: startDate?.toISOString().split('T')[0] || '',
          created_at_before: endDate?.toISOString().split('T')[0] || ''
        }
      });
      if (response) {
        setProcurements(response?.data?.data?.data);
        setLastPage(Math.ceil(response?.data?.data?.pagination.total / 10));
      }
    } catch (err: any) {
      toast(
        <ToastMessage
          message={err.response?.data?.message || 'Error fetching procurements'}
          type='error'
        />
      );
    } finally {
      setIsFetchingProcurements(false);
    }
  };

  const debounceFetchProcurements = useCallback(debounce(fetchProcurements, 1000), []);

  useEffect(() => {
    setIsFetchingProcurements(true);
    setProcurements([]);
    debounceFetchProcurements(
      filters.search,
      filters.status,
      filters.current_page,
      filters.dateRange?.startDate,
      filters.dateRange?.endDate
    );
  }, [filters, activeStore?.id]);

  const initSocket = async () => {
    const conn = await SocketIOConnection.getConnection();
    const roomName = `kitchen_request:${activeStore?.id}`;

    conn.onReconnect(async () => {
      conn.joinRoom(roomName, process.env.REACT_APP_WEBOCKETAPP_KEY);
    });

    conn.addEventListener(roomName, 'kitchen_request_updated', (data: RequestType) => {
      setProcurements((prev) => {
        const findRequest = prev?.find((item) => item.id === data.id);
        if (findRequest) {
          return prev?.map((item) => (item.id === data.id ? { ...item, ...data } : item));
        } else {
          return [data, ...prev];
        }
      });
    });
    conn.joinRoom(roomName, process.env.REACT_APP_WEBOCKETAPP_KEY);
    return () => {
      conn.removeListener(roomName, ' kitchen_request_updated');
    };
  };
  const removeListener = async () => {
    const conn = await SocketIOConnection.getConnection();
    const roomName = `kitchen_request:${activeStore?.id}`;
    conn.removeListener(roomName, 'kitchen_request_updated');
  };
  useEffect(() => {
    if (!activeStore?.id) return;
    initSocket();
    return () => {
      removeListener();
    };
  }, []);

  return (
    <div>
      <PageHeaderText title='Internal Procurement' description='Manage inventory item requests'>
        {canWriteProcurement && (
          <Link to={MAIN_STORE_ROUTE_KEYS.NEW_PROCUREMENT}>
            <Button variant='block' color='primary' className='px-6 py-2 text-sm'>
              New Internal Procurement
            </Button>
          </Link>
        )}
      </PageHeaderText>

      <RequestListFilters
        statusMode='multi'
        statusOptions={[
          { text: ENUM_REQUEST_STATUS.APPROVED, value: ENUM_REQUEST_STATUS.APPROVED },
          { text: ENUM_REQUEST_STATUS.REJECTED, value: ENUM_REQUEST_STATUS.REJECTED },
          { text: ENUM_REQUEST_STATUS.RETURNED, value: ENUM_REQUEST_STATUS.RETURNED },
          { text: ENUM_REQUEST_STATUS.RECEIVED, value: ENUM_REQUEST_STATUS.RECEIVED }
        ]}
        filters={filters}
        onFilterChange={setFilters}
        pageCount={lastPage}
      />
      {isFetchingProcurements ? (
        <div className='pt-10'>
          <ActivityIndicator />
        </div>
      ) : procurements.length === 0 ? (
        <div className='grid place-items-center mt-24'>
          <NoProcurement className='mb-5' />
          <p className='font-medium text-[#5E6164]'>You are yet to make any procurement</p>
        </div>
      ) : (
        <div className='mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
          {procurements.map((p, i) => (
            <KitchenProcurementCard key={i} procurement={p} />
          ))}
        </div>
      )}
    </div>
  );
};

export default KitchenProcurementList;
